<template>
  <div class="api-config-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('apiManagement.title') }}</h2>
        <div class="header-actions">
          <a-space>
            <a-button type="primary" @click="showCreateModal">
              <template #icon><PlusOutlined /></template>
              {{ $t('apiManagement.createApi') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <a-row :gutter="16" class="stats-row">
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('apiManagement.totalApis')" :value="stats.total_apis" />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('apiManagement.activeApis')" :value="stats.active_apis" />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('apiManagement.inactiveApis')" :value="stats.inactive_apis" />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('apiManagement.successRate')" :value="95.8" suffix="%" />
        </a-card>
      </a-col>
    </a-row>

    <!-- 搜索和筛选 -->
    <a-card class="search-card">
      <a-form layout="inline" :model="filters">

        <a-form-item :label="$t('apiManagement.apiType')">
          <a-select
            v-model:value="filters.api_type"
            :placeholder="$t('apiManagement.apiType')"
            style="width: 120px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option
              v-for="option in apiTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('apiManagement.status')">
          <a-select
            v-model:value="filters.status"
            :placeholder="$t('apiManagement.status')"
            style="width: 100px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option :value="true">{{ $t('apiManagement.enabled') }}</a-select-option>
            <a-select-option :value="false">{{ $t('apiManagement.disabled') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('common.search')">
          <a-input-search
            v-model:value="filters.search"
            :placeholder="$t('apiManagement.searchApiName')"
            style="width: 200px"
            @search="handleFilterChange"
            @change="handleSearchChange"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button @click="refreshData">
              <template #icon><ReloadOutlined /></template>
              {{ $t('apiManagement.refresh') }}
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- API配置列表 -->
    <a-card>
      <a-table
        :columns="columns"
        :data-source="apiConfigs"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'api_type'">
            <a-tag :color="getAPITypeColor(record.api_type)">
              {{ getAPITypeLabel(record.api_type) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'usage_type'">
            <a-tag :color="getUsageTypeColor(record.usage_type)">
              {{ getUsageTypeLabel(record.usage_type) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-switch
              :checked="record.status"
              @change="(checked: boolean) => handleStatusToggle(record, checked)"
              :loading="record.statusLoading"
            />
          </template>
          <template v-else-if="column.key === 'api_key_masked'">
            <a-typography-text code>{{ record.api_key_masked }}</a-typography-text>
          </template>
          <template v-else-if="column.key === 'created_at'">
            {{ formatDateTime(record.created_at) }}
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="showEditModal(record)">
                {{ $t('apiManagement.edit') }}
              </a-button>
              <a-popconfirm
                :title="$t('apiManagement.confirmDelete')"
                @confirm="handleDelete(record)"
              >
                <a-button type="link" size="small" danger>
                  {{ $t('apiManagement.delete') }}
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 创建/编辑API配置模态框 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? $t('apiManagement.editTitle') : $t('apiManagement.createTitle')"
      :confirm-loading="modalLoading"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      width="600px"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        layout="vertical"
      >
        <a-form-item :label="$t('apiManagement.apiName')" name="name">
          <a-input v-model:value="formData.name" :placeholder="$t('apiManagement.enterApiName')" />
        </a-form-item>
        
        <a-form-item :label="$t('apiManagement.apiUrl')" name="url">
          <a-input v-model:value="formData.url" :placeholder="$t('apiManagement.enterApiUrl')" />
        </a-form-item>
        
        <a-form-item :label="$t('apiManagement.apiKey')" name="api_key">
          <a-input-password
            v-model:value="formData.api_key"
            :placeholder="isEdit ? $t('apiManagement.apiKeyKeepEmpty') : $t('apiManagement.enterApiKey')"
          />
        </a-form-item>

        <a-form-item :label="$t('apiManagement.region')" name="region">
          <a-input
            v-model:value="formData.region"
            :placeholder="$t('apiManagement.regionPlaceholder')"
            allow-clear
          />
        </a-form-item>
        
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('apiManagement.apiType')" name="api_type">
              <a-select v-model:value="formData.api_type" :placeholder="$t('apiManagement.selectApiType')">
                <a-select-option
                  v-for="option in apiTypeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('apiManagement.usageType')" name="usage_type">
              <a-select v-model:value="formData.usage_type" :placeholder="$t('apiManagement.selectUsageType')">
                <a-select-option
                  v-for="option in usageTypeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
        
        <a-form-item :label="$t('apiManagement.status')" name="status">
          <a-switch v-model:checked="formData.status" />
          <span style="margin-left: 8px;">
            {{ formData.status ? $t('apiManagement.enabled') : $t('apiManagement.disabled') }}
          </span>
        </a-form-item>
        
        <template v-if="isAiModelForm(formData.api_type)">
          <a-form-item :label="$t('apiManagement.modelName')" name="model_name">
            <a-input
              v-model:value="formData.model_name"
              :placeholder="$t('apiManagement.modelNameHint')"
            />
          </a-form-item>
          <a-form-item :label="$t('apiManagement.maxTokens')" name="max_tokens">
            <a-input-number
              v-model:value="formData.max_tokens"
              :min="1"
              :max="65536"
              :placeholder="$t('apiManagement.maxTokensPlaceholder')"
              style="width: 100%"
            />
          </a-form-item>
          <a-form-item :label="$t('apiManagement.extraConfig')" name="extra_config_json">
            <a-textarea
              v-model:value="formData.extra_config_json"
              :placeholder="$t('apiManagement.extraConfigPlaceholder')"
              :rows="8"
            />
          </a-form-item>
        </template>

        <a-form-item :label="$t('apiManagement.description')" name="description">
          <a-textarea
            v-model:value="formData.description"
            :placeholder="$t('apiManagement.enterDescription')"
            :rows="3"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { message } from 'ant-design-vue'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons-vue'
import type { TableColumnsType, FormInstance } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import {
  getApiConfigs,
  getApiStats,
  createApiConfig,
  updateApiConfig,
  deleteApiConfig,
  toggleApiStatus,
  type APIConfig,
  type APIConfigStats,
  type APIConfigCreate,
  type APIConfigUpdate
} from '@/modules/system/api/api-config'
import { formatDateTime } from '@/shared/utils'
import { isAiModelForm, parseExtraConfigJson } from './api-config-ai-model-form'

const { t } = useI18n()

// 响应式数据
const loading = ref(false)
const modalVisible = ref(false)
const modalLoading = ref(false)
const isEdit = ref(false)
const formRef = ref<FormInstance>()

// 国际化的选项
const apiTypeOptions = computed(() => [
  { label: t('apiManagement.translationApi'), value: 'translation' },
  { label: t('apiManagement.speechApi'), value: 'speech' },
  { label: t('apiManagement.aiModelApi'), value: 'ai_model' }
])

const usageTypeOptions = computed(() => [
  { label: t('apiManagement.characterType'), value: 'character' },
  { label: t('apiManagement.timeType'), value: 'time' }
])

// API配置列表
const apiConfigs = ref<APIConfig[]>([])
const stats = ref<APIConfigStats>({
  total_apis: 0,
  active_apis: 0,
  inactive_apis: 0,
  by_type: {},
  by_usage_type: {}
})

// 过滤条件
const filters = reactive({
  api_type: undefined as string | undefined,
  status: undefined as boolean | undefined,
  search: ''
})

// 分页配置
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number) => t('apiManagement.paginationTotal', { total }),
  // 使用 onChange 处理页码变化
  onChange: (page: number, pageSize: number) => {
    pagination.current = page
    pagination.pageSize = pageSize
    loadAPIConfigs()
  },
  // 使用 onShowSizeChange 处理每页数量变化
  onShowSizeChange: (current: number, size: number) => {
    pagination.current = current
    pagination.pageSize = size
    loadAPIConfigs()
  }
})

// 表单数据
const formData = reactive<APIConfigCreate & { id?: number; extra_config_json?: string }>({
  name: '',
  url: '',
  api_key: '',
  api_type: 'translation',
  usage_type: 'character',
  status: true,
  region: '',
  description: '',
  model_name: '',
  max_tokens: undefined,
  extra_config_json: ''
})

// 表单验证规则
const formRules = computed(() => ({
  name: [
    { required: true, message: t('apiManagement.apiNameRequired'), trigger: 'blur' },
    { min: 1, max: 100, message: t('apiManagement.apiNameLength'), trigger: 'blur' }
  ],
  url: [
    { required: true, message: t('apiManagement.apiUrlRequired'), trigger: 'blur' },
    { pattern: /^https?:\/\//, message: t('apiManagement.apiUrlPattern'), trigger: 'blur' }
  ],
  api_key: [
    { 
      required: !isEdit.value, 
      message: t('apiManagement.apiKeyRequired'), 
      trigger: 'blur' 
    }
  ],
  api_type: [
    { required: true, message: t('apiManagement.apiTypeRequired'), trigger: 'change' }
  ],
  usage_type: [
    { required: true, message: t('apiManagement.usageTypeRequired'), trigger: 'change' }
  ]
}))

// 表格列配置
const columns = computed<TableColumnsType>(() => [
  {
    title: t('common.serialNumber'),
    key: 'index',
    width: 60,
    customRender: ({ index }) => index + 1 + (pagination.current - 1) * pagination.pageSize
  },
  {
    title: t('apiManagement.apiName'),
    dataIndex: 'name',
    key: 'name',
    width: 150
  },
  {
    title: t('apiManagement.apiUrl'),
    dataIndex: 'url',
    key: 'url',
    ellipsis: true,
    width: 200
  },
  {
    title: t('apiManagement.region'),
    dataIndex: 'region',
    key: 'region',
    width: 120,
    ellipsis: true
  },
  {
    title: t('apiManagement.apiKey'),
    dataIndex: 'api_key_masked',
    key: 'api_key_masked',
    width: 120
  },
  {
    title: t('apiManagement.apiType'),
    dataIndex: 'api_type',
    key: 'api_type',
    width: 100
  },
  {
    title: t('apiManagement.usageType'),
    dataIndex: 'usage_type',
    key: 'usage_type',
    width: 100
  },
  {
    title: t('apiManagement.status'),
    dataIndex: 'status',
    key: 'status',
    width: 80
  },
  {
    title: t('apiManagement.createTime'),
    dataIndex: 'created_at',
    key: 'created_at',
    width: 160
  },
  {
    title: t('apiManagement.description'),
    dataIndex: 'description',
    key: 'description',
    ellipsis: true
  },
  {
    title: t('common.actions'),
    key: 'action',
    width: 120,
    fixed: 'right'
  }
])

// 获取API类型标签颜色
const getAPITypeColor = (type: string) => {
  const colors = {
    translation: 'blue',
    speech: 'green',
    ai_model: 'purple'
  }
  return colors[type as keyof typeof colors] || 'default'
}

// 获取API类型标签文本
const getAPITypeLabel = (type: string) => {
  const option = apiTypeOptions.value.find(opt => opt.value === type)
  return option?.label || type
}

// 获取用量类型标签颜色
const getUsageTypeColor = (type: string) => {
  const colors = {
    character: 'orange',
    time: 'cyan'
  }
  return colors[type as keyof typeof colors] || 'default'
}

// 获取用量类型标签文本
const getUsageTypeLabel = (type: string) => {
  const option = usageTypeOptions.value.find(opt => opt.value === type)
  return option?.label || type
}

// 加载API配置列表
const loadAPIConfigs = async () => {
  try {
    loading.value = true
    const params = {
      page: pagination.current,
      size: pagination.pageSize,
      ...filters
    }
    
    const response = await getApiConfigs(params)
    if (!response?.items || !Array.isArray(response.items)) {
      console.error('API 配置列表响应格式异常:', response)
      message.error(t('apiManagement.loadApiListFailed'))
      apiConfigs.value = []
      pagination.total = 0
      return
    }
    apiConfigs.value = response.items
    pagination.total = response.total
  } catch (error) {
    console.error('Load API configs error:', error)
  } finally {
    loading.value = false
  }
}

// 加载统计信息
const loadStats = async () => {
  try {
    stats.value = await getApiStats()
  } catch (error) {
    console.error('Load stats error:', error)
  }
}

// 刷新数据
const refreshData = () => {
  loadAPIConfigs()
  loadStats()
}

// 处理表格变化（排序和筛选）
// 注意：分页变化已经通过 pagination 配置的 onChange/onShowSizeChange 处理
// @change 事件会传递三个参数：(pagination, filters, sorter)
const handleTableChange = (pag: any, filters: any, sorter: any) => {
  // 分页变化已经在 pagination 的 onChange/onShowSizeChange 中处理
  // 这里只处理排序和筛选（如果需要的话）
}

// 处理过滤条件变化
const handleFilterChange = () => {
  pagination.current = 1
  loadAPIConfigs()
}

// 处理搜索输入变化
const handleSearchChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  filters.search = target.value
}

// 显示创建模态框
const showCreateModal = () => {
  isEdit.value = false
  resetFormData()
  modalVisible.value = true
}

// 显示编辑模态框
const showEditModal = (record: APIConfig) => {
  isEdit.value = true
  Object.assign(formData, {
    id: record.id,
    name: record.name,
    url: record.url,
    api_key: '', // 编辑时不显示原密钥
    api_type: record.api_type,
    usage_type: record.usage_type,
    status: record.status,
    region: record.region || '',
    description: record.description,
    model_name: record.model_name || '',
    max_tokens: record.max_tokens ?? undefined,
    extra_config_json: record.extra_config
      ? JSON.stringify(record.extra_config, null, 2)
      : ''
  })
  modalVisible.value = true
}

// 重置表单数据
const resetFormData = () => {
  Object.assign(formData, {
    id: undefined,
    name: '',
    url: '',
    api_key: '',
    api_type: 'translation',
    usage_type: 'character',
    status: true,
    region: '',
    description: '',
    model_name: '',
    max_tokens: undefined,
    extra_config_json: ''
  })
}

// 处理模态框确定
const handleModalOk = async () => {
  try {
    await formRef.value?.validate()

    let extraConfig: Record<string, unknown> | undefined
    if (isAiModelForm(formData.api_type)) {
      try {
        extraConfig = parseExtraConfigJson(formData.extra_config_json || '')
      } catch {
        message.error(t('apiManagement.extraConfigInvalid'))
        return
      }
    }

    modalLoading.value = true

    const payload: APIConfigCreate | APIConfigUpdate = {
      name: formData.name,
      url: formData.url,
      api_type: formData.api_type,
      usage_type: formData.usage_type,
      status: formData.status,
      region: formData.region?.trim() || null,
      description: formData.description
    }
    if (!isEdit.value || formData.api_key) {
      payload.api_key = formData.api_key
    }
    if (isAiModelForm(formData.api_type)) {
      payload.model_name = formData.model_name
      // null clears stored value; omit would leave previous on update (exclude_unset)
      payload.max_tokens = formData.max_tokens ?? null
      payload.extra_config = extraConfig
    }
    
    if (isEdit.value) {
      await updateApiConfig(formData.id!, payload as APIConfigUpdate)
      message.success(t('apiManagement.updateSuccess'))
    } else {
      await createApiConfig(payload as APIConfigCreate)
      message.success(t('apiManagement.createSuccess'))
    }
    
    modalVisible.value = false
    refreshData()
  } catch (error) {
    console.error('Save API config error:', error)
  } finally {
    modalLoading.value = false
  }
}

// 处理模态框取消
const handleModalCancel = () => {
  modalVisible.value = false
  formRef.value?.resetFields()
}

// 处理状态切换
const handleStatusToggle = async (record: APIConfig & { statusLoading?: boolean }, checked: boolean) => {
  try {
    record.statusLoading = true
    await toggleApiStatus(record.id)
    record.status = checked
    const statusText = checked ? t('apiManagement.enabled') : t('apiManagement.disabled')
    message.success(t('apiManagement.toggleSuccess', { status: statusText }))
    loadStats() // 更新统计信息
  } catch (error) {
    console.error('Toggle status error:', error)
  } finally {
    record.statusLoading = false
  }
}

// 处理删除
const handleDelete = async (record: APIConfig) => {
  try {
    await deleteApiConfig(record.id)
    message.success(t('apiManagement.deleteSuccess'))
    refreshData()
  } catch (error) {
    console.error('Delete API config error:', error)
  }
}

// 组件挂载时加载数据
onMounted(() => {
  refreshData()
})
</script>

<style scoped lang="less">
.api-config-management {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .header-actions {
      flex-shrink: 0;
    }
  }
}

.stats-row {
  margin-bottom: 24px;
}

.search-card {
  margin-bottom: 24px;
}

:deep(.ant-table-tbody > tr > td) {
  padding: 12px 16px;
}
</style>